/**
 * SellerCardsRow — the vertical stack of inline quote `seller_match` cards
 * under an assistant quote-review answer (design revamp).
 *
 * The prototype (`screens-chat.jsx`) renders the seller cards as a
 * `flex-direction: column; gap: 8` stack INSIDE the assistant bubble. Entrance
 * motion is a gated stagger; the cards themselves are informational
 * (`SellerMiniCard`).
 */
import { motion, useReducedMotion, type Variants } from "framer-motion";
import { type ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { SellerMiniCard } from "@/features/chat/components/SellerMiniCard";
import type { SellerMatchCardData } from "@/features/chat/useChatStream";

const listVariants: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.04 } },
};
const itemVariants: Variants = {
  hidden: { opacity: 0, y: 6 },
  show: { opacity: 1, y: 0, transition: { duration: 0.2, ease: "easeOut" } },
};

export interface SellerCardsRowProps {
  cards: SellerMatchCardData[];
}

export function SellerCardsRow({
  cards,
}: SellerCardsRowProps): ReactElement | null {
  const { t } = useTranslation("chat");
  const prefersReduced = useReducedMotion() ?? false;
  if (cards.length === 0) return null;

  return (
    <motion.div
      data-testid="chat-seller-cards"
      aria-label={t("seller.row")}
      role="group"
      className="mt-3 flex flex-col gap-2"
      variants={prefersReduced ? undefined : listVariants}
      initial={prefersReduced ? false : "hidden"}
      animate={prefersReduced ? undefined : "show"}
    >
      {cards.map((card, i) => (
        <motion.div
          key={`${card.seller_id}-${i}`}
          variants={prefersReduced ? undefined : itemVariants}
        >
          <SellerMiniCard card={card} />
        </motion.div>
      ))}
    </motion.div>
  );
}

export default SellerCardsRow;
